import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { integrationLogService } from '@/services/integration-log-service';
import type { IntegrationLogEntry, IntegrationLogFilters, IntegrationLogStatus, IntegrationLogSource } from '@/types/integration-log';
import { useIntegration } from '@/providers/IntegrationProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Search, 
  Filter, 
  AlertCircle, 
  AlertTriangle, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  Calendar,
  X,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';

const statusConfig: Record<IntegrationLogStatus, { label: string; icon: typeof AlertCircle; variant: 'destructive' | 'warning' | 'default'; className: string }> = {
  success: {
    label: 'Success',
    icon: CheckCircle2,
    variant: 'default' as const,
    className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  },
  error: {
    label: 'Error',
    icon: AlertCircle,
    variant: 'destructive' as const,
    className: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  },
  warning: {
    label: 'Warning',
    icon: AlertTriangle,
    variant: 'warning' as const,
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  },
};

const sourceConfig: Record<IntegrationLogSource, { label: string; color: string }> = {
  financials: { label: 'Access Financials', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' },
  dimensions: { label: 'Access Dimensions', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' },
  xero: { label: 'Xero', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400' },
  quickbooks: { label: 'QuickBooks', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
  system: { label: 'System', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400' },
};

function mapIntegrationTypeToLogSource(integrationType: string): IntegrationLogSource | null {
  const mapping: Record<string, IntegrationLogSource> = {
    'access-financials': 'financials',
    'access-dimensions': 'dimensions',
    'xero': 'xero',
    'quickbooks': 'quickbooks',
  };
  return mapping[integrationType] ?? null;
}

export default function IntegrationLogPage(): React.ReactElement {
  const navigate = useNavigate();
  const { settings: integrationSettings } = useIntegration();
  const [logs, setLogs] = useState<IntegrationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<IntegrationLogEntry | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvingLog, setResolvingLog] = useState<IntegrationLogEntry | null>(null);
  const cleanupPerformedRef = useRef(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<IntegrationLogStatus[]>([]);
  const [sourceFilter, setSourceFilter] = useState<IntegrationLogSource[]>([]);
  const [resolvedFilter, setResolvedFilter] = useState<'all' | 'resolved' | 'unresolved'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  // Cleanup old logs on page load (only once per page visit)
  useEffect(() => {
    if (cleanupPerformedRef.current) return;
    cleanupPerformedRef.current = true;
    
    integrationLogService.cleanupOldLogs(30).catch(() => {
      // Silently ignore cleanup errors
    });
  }, []);

  // Determine the effective source filter based on active integration
  const getEffectiveSourceFilter = useCallback((): IntegrationLogSource[] | undefined => {
    // If user has manually set a source filter, use that
    if (sourceFilter.length > 0) {
      return sourceFilter;
    }
    
    // If there's an active integration, filter by its source
    if (integrationSettings?.enabled && integrationSettings.integrationType) {
      const mappedSource = mapIntegrationTypeToLogSource(integrationSettings.integrationType);
      if (mappedSource) {
        return [mappedSource];
      }
    }
    
    // No active integration or no mapping, show all
    return undefined;
  }, [sourceFilter, integrationSettings]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const effectiveSourceFilter = getEffectiveSourceFilter();

      const filters: IntegrationLogFilters = {
        search: searchQuery || undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        source: effectiveSourceFilter,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        resolved: resolvedFilter === 'all' ? undefined : resolvedFilter === 'resolved',
      };

      const response = await integrationLogService.getLogs(currentPage, pageSize, filters);
      setLogs(response.data);
      setTotalCount(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err) {
      console.error('Failed to load integration logs:', err);
      setError('Failed to load integration logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, getEffectiveSourceFilter, resolvedFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sourceFilter, resolvedFilter, dateFrom, dateTo]);

  const handleViewDetails = (log: IntegrationLogEntry) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleResolveClick = (log: IntegrationLogEntry) => {
    setResolvingLog(log);
    setResolveNotes('');
    setResolveDialogOpen(true);
  };

  const handleResolve = async () => {
    if (!resolvingLog) return;

    try {
      await integrationLogService.resolveLog(resolvingLog.id, resolveNotes);
      toast.success('Log entry marked as resolved');
      setResolveDialogOpen(false);
      setResolvingLog(null);
      setResolveNotes('');
      loadLogs();
    } catch (err) {
      console.error('Failed to resolve log:', err);
      toast.error('Failed to resolve log entry');
    }
  };

  const handleUnresolve = async (log: IntegrationLogEntry) => {
    try {
      await integrationLogService.unresolveLog(log.id);
      toast.success('Log entry marked as unresolved');
      loadLogs();
    } catch (err) {
      console.error('Failed to unresolve log:', err);
      toast.error('Failed to unresolve log entry');
    }
  };

  const toggleStatusFilter = (status: IntegrationLogStatus) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleSourceFilter = (source: IntegrationLogSource) => {
    setSourceFilter(prev =>
      prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setSourceFilter([]);
    setResolvedFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || statusFilter.length > 0 || sourceFilter.length > 0 || 
    resolvedFilter !== 'all' || dateFrom || dateTo;

  const navigateToTransaction = (transactionId: string) => {
    navigate(`/enquiry?transactionId=${transactionId}`);
  };

  const navigateToAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const renderTableContent = () => {
    if (error) {
      return (
        <Card className="mx-6 mb-6">
          <CardContent className="py-12">
            <div className="text-center text-destructive">
              <AlertCircle className="h-12 w-12 mx-auto mb-4" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (loading) {
      return (
        <Card className="mx-6 mb-6">
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>Loading integration logs...</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (logs.length === 0) {
      return (
        <Card className="mx-6 mb-6">
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No integration logs found</p>
              <p className="text-sm mt-1">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Integration logs will appear here'}
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mx-6 mb-6">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[150px]">Source</TableHead>
                  <TableHead className="w-[120px]">Error Code</TableHead>
                  <TableHead className="min-w-[300px]">Message</TableHead>
                  <TableHead className="w-[120px]">Transaction</TableHead>
                  <TableHead className="w-[120px]">Asset</TableHead>
                  <TableHead className="w-[100px]">Resolved</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const statusInfo = statusConfig[log.status];
                  const StatusIcon = statusInfo.icon;
                  const sourceInfo = sourceConfig[log.source];

                  return (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant} className={cn('gap-1', statusInfo.className)}>
                          <StatusIcon className="h-3 w-3" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={sourceInfo.color}>
                          {sourceInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.errorCode ?? '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="max-w-[300px] truncate" title={log.message}>
                          {log.message}
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.transactionId ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => navigateToTransaction(log.transactionId!)}
                          >
                            {log.transactionId}
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.assetCode ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => log.assetId && navigateToAsset(log.assetId)}
                          >
                            {log.assetCode}
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.resolved ? (
                          <Badge variant="outline" className="gap-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                            <CheckCircle2 className="h-3 w-3" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1">
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                          >
                            Details
                          </Button>
                          {!log.resolved ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResolveClick(log)}
                            >
                              Resolve
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnresolve(log)}
                            >
                              Unresolve
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Integration Log</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Historical audit trail for integration errors and warnings
              </p>
            </div>
          </div>
          <EvoFeatureFeedbackCollector feature="Integration Log" />
        </div>
      </div>

            {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <div className="space-y-6">
          {/* Filters */}
          <Card className="m-6 mb-0">
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Search and Quick Filters */}
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search by message, asset code, transaction ID, or error code..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select value={resolvedFilter} onValueChange={(value: any) => setResolvedFilter(value)}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="unresolved">Unresolved Only</SelectItem>
                        <SelectItem value="resolved">Resolved Only</SelectItem>
                      </SelectContent>
                    </Select>
                    {hasActiveFilters && (
                      <Button variant="outline" size="icon" onClick={clearFilters} title="Clear all filters">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Status Filters */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Status:
                  </span>
                  {Object.entries(statusConfig).map(([status, config]) => {
                    const Icon = config.icon;
                    const isActive = statusFilter.includes(status as IntegrationLogStatus);
                    return (
                      <Button
                        key={status}
                        variant={isActive ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleStatusFilter(status as IntegrationLogStatus)}
                        className="gap-2"
                      >
                        <Icon className="h-4 w-4" />
                        {config.label}
                      </Button>
                    );
                  })}
                </div>

                {/* Source Filters */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Source:
                  </span>
                  {Object.entries(sourceConfig).map(([source, config]) => {
                    const isActive = sourceFilter.includes(source as IntegrationLogSource);
                    return (
                      <Button
                        key={source}
                        variant={isActive ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleSourceFilter(source as IntegrationLogSource)}
                      >
                        {config.label}
                      </Button>
                    );
                  })}
                </div>

                {/* Date Range */}
                <div className="flex flex-wrap gap-4 items-center">
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Date Range:
                  </span>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-[160px]"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-[160px]"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Summary */}
          <div className="flex items-center justify-between mx-6">
            <p className="text-sm text-muted-foreground">
              Showing {logs.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to{' '}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
            </p>
          </div>

          {/* Table */}
          {renderTableContent()}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mx-6 mb-6">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Integration Log Details</DialogTitle>
            <DialogDescription>
              Detailed information about this integration log entry
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Timestamp</Label>
                  <p className="text-sm font-medium">
                    {format(new Date(selectedLog.timestamp), 'PPpp')}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={statusConfig[selectedLog.status].variant} className={statusConfig[selectedLog.status].className}>
                      {statusConfig[selectedLog.status].label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Source</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className={sourceConfig[selectedLog.source].color}>
                      {sourceConfig[selectedLog.source].label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Error Code</Label>
                                    <p className="text-sm font-mono font-medium">
                                      {selectedLog.errorCode ?? '-'}
                                    </p>
                </div>
                {selectedLog.transactionId && (
                  <div>
                    <Label className="text-muted-foreground">Transaction ID</Label>
                    <p className="text-sm font-medium">{selectedLog.transactionId}</p>
                  </div>
                )}
                {selectedLog.transactionType && (
                  <div>
                    <Label className="text-muted-foreground">Transaction Type</Label>
                    <p className="text-sm font-medium">{selectedLog.transactionType}</p>
                  </div>
                )}
                {selectedLog.assetCode && (
                  <div>
                    <Label className="text-muted-foreground">Asset Code</Label>
                    <p className="text-sm font-medium">{selectedLog.assetCode}</p>
                  </div>
                )}
                {selectedLog.userName && (
                  <div>
                    <Label className="text-muted-foreground">User</Label>
                    <p className="text-sm font-medium">{selectedLog.userName}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Message</Label>
                <p className="text-sm mt-1">{selectedLog.message}</p>
              </div>

              {selectedLog.details && (
                <div>
                  <Label className="text-muted-foreground">Details</Label>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{selectedLog.details}</p>
                </div>
              )}

              {selectedLog.resolved && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Resolution Information</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm">
                        Resolved on {format(new Date(selectedLog.resolvedAt!), 'PPpp')}
                      </span>
                    </div>
                    {selectedLog.resolvedBy && (
                      <p className="text-sm text-muted-foreground">
                        Resolved by: {selectedLog.resolvedBy}
                      </p>
                    )}
                    {selectedLog.notes && (
                      <div>
                        <Label className="text-muted-foreground">Notes</Label>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{selectedLog.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Integration Log</DialogTitle>
            <DialogDescription>
              Mark this log entry as resolved and add optional notes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="resolve-notes">Resolution Notes (Optional)</Label>
              <Textarea
                id="resolve-notes"
                placeholder="Add notes about how this issue was resolved..."
                value={resolveNotes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setResolveNotes(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResolve}>
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
